'use client';

import { resolvedFlagDescription } from '@/@feature-flags/catalog';
import type { FeatureFlag, FeatureFlagOverride } from '@/@feature-flags/types/entities';
import { formatDateTime } from '@/lib/utils/formatting';
import {
    Button,
    Chip,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Tooltip,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { RowActions, STAFF_TABLE } from '@/components/staff-tools/staff-table';

interface FlagDetailPanelProps {
    flag: FeatureFlag | null;
    isLoading: boolean;
    onAddOverride: () => void;
    onRemoveOverride: (overrideId: number) => void;
    isRemovingOverride: boolean;
    onClose: () => void;
}

export function FlagDetailPanel({
    flag,
    isLoading,
    onAddOverride,
    onRemoveOverride,
    isRemovingOverride,
    onClose,
}: FlagDetailPanelProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!flag) {
        return (
            <div className="py-8 text-center text-foreground-600">
                Select a feature flag to view details
            </div>
        );
    }

    const overrides = flag.overrides ?? [];
    const description = resolvedFlagDescription(flag.key, flag.description);
    const userOverrides = overrides.filter((o) => o.orgUserId);
    const orgOverrides = overrides.filter((o) => o.orgId && !o.orgUserId);
    const groupOverrides = overrides.filter((o) => o.cognitoGroup);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="flex items-center gap-2 font-mono text-small font-semibold">
                        {flag.key}
                    </h3>
                    {description.text ? (
                        <p className="mt-1 text-small text-foreground-600">{description.text}</p>
                    ) : null}
                </div>
                <Button isIconOnly size="sm" variant="light" onPress={onClose} aria-label="Close">
                    <Icon icon="lucide:x" className="h-4 w-4" />
                </Button>
            </div>

            <div className="flex flex-wrap gap-2">
                <Chip
                    size="sm"
                    variant="flat"
                    color={flag.enabledInternal ? 'success' : 'default'}
                    startContent={
                        <Icon
                            icon={flag.enabledInternal ? 'lucide:check' : 'lucide:x'}
                            className="h-3 w-3"
                        />
                    }
                >
                    Internal
                </Chip>
                <Chip
                    size="sm"
                    variant="flat"
                    color={flag.enabledExternal ? 'success' : 'default'}
                    startContent={
                        <Icon
                            icon={flag.enabledExternal ? 'lucide:check' : 'lucide:x'}
                            className="h-3 w-3"
                        />
                    }
                >
                    External
                </Chip>
                {flag.releaseEnv === null ? (
                    <Tooltip
                        content="No environment set — affects all environments sharing this database."
                        delay={300}
                    >
                        <Chip size="sm" variant="flat" color="warning" className="cursor-default">
                            global
                        </Chip>
                    </Tooltip>
                ) : (
                    <Chip size="sm" variant="flat" color="secondary">
                        {flag.releaseEnv}
                    </Chip>
                )}
            </div>

            <div className="text-tiny text-foreground-600">
                Created {formatDateTime(flag.createdAt)} · Updated {formatDateTime(flag.updatedAt)}
            </div>

            {/* Overrides */}
            <div className="mt-2 border-t border-default-200/50 pt-4">
                <div className="mb-3 flex items-center justify-between">
                    <h4 className="flex items-center gap-2 text-tiny font-semibold uppercase tracking-wide text-foreground-600">
                        <Icon icon="lucide:layers" className="h-4 w-4 text-secondary" />
                        Overrides
                        <Chip size="sm" variant="flat">
                            {overrides.length}
                        </Chip>
                    </h4>
                    <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        onPress={onAddOverride}
                        startContent={<Icon icon="lucide:plus" className="h-3.5 w-3.5" />}
                    >
                        Add Override
                    </Button>
                </div>

                {overrides.length === 0 ? (
                    <p className="py-4 text-center text-small text-foreground-600">
                        No overrides configured
                    </p>
                ) : (
                    <div className="flex flex-col gap-4">
                        {userOverrides.length > 0 && (
                            <OverrideSection
                                title="User Overrides"
                                icon="lucide:user"
                                overrides={userOverrides}
                                onRemove={onRemoveOverride}
                                isRemoving={isRemovingOverride}
                            />
                        )}
                        {orgOverrides.length > 0 && (
                            <OverrideSection
                                title="Org Overrides"
                                icon="lucide:building-2"
                                overrides={orgOverrides}
                                onRemove={onRemoveOverride}
                                isRemoving={isRemovingOverride}
                            />
                        )}
                        {groupOverrides.length > 0 && (
                            <OverrideSection
                                title="Group Overrides"
                                icon="lucide:shield"
                                overrides={groupOverrides}
                                onRemove={onRemoveOverride}
                                isRemoving={isRemovingOverride}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function OverrideSection({
    title,
    icon,
    overrides,
    onRemove,
    isRemoving,
}: {
    title: string;
    icon: string;
    overrides: FeatureFlagOverride[];
    onRemove: (id: number) => void;
    isRemoving: boolean;
}) {
    const getTargetColumnLabel = () => {
        const first = overrides[0];
        if (first?.cognitoGroup) return 'Group';
        if (first?.orgUserId) return 'User';
        return 'Organization';
    };

    const getTargetValue = (override: FeatureFlagOverride) => {
        if (override.cognitoGroup) return override.cognitoGroup;
        if (override.orgUserId) return override.orgUserEmail ?? String(override.orgUserId);
        return override.orgName ?? String(override.orgId);
    };

    return (
        <div>
            <h5 className="mb-2 flex items-center gap-1.5 text-tiny font-semibold uppercase tracking-wide text-foreground-600">
                <Icon icon={icon} className="h-3.5 w-3.5" />
                {title}
            </h5>
            <Table
                aria-label={title}
                removeWrapper
                isCompact
                classNames={{ th: STAFF_TABLE.th, td: STAFF_TABLE.td }}
            >
                <TableHeader>
                    <TableColumn>{getTargetColumnLabel()}</TableColumn>
                    <TableColumn>Access</TableColumn>
                    <TableColumn>Created</TableColumn>
                    <TableColumn width={60} align="end">
                        <span className="sr-only">Actions</span>
                    </TableColumn>
                </TableHeader>
                <TableBody>
                    {overrides.map((override) => (
                        <TableRow key={override.id}>
                            <TableCell>
                                <span className="font-mono text-small">
                                    {getTargetValue(override)}
                                </span>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    size="sm"
                                    variant="flat"
                                    color={override.enabled ? 'success' : 'danger'}
                                >
                                    {override.enabled ? 'Granted' : 'Denied'}
                                </Chip>
                            </TableCell>
                            <TableCell>
                                <span className="text-tiny text-foreground-600">
                                    {formatDateTime(override.createdAt)}
                                </span>
                            </TableCell>
                            <TableCell>
                                <RowActions
                                    label={`override ${override.id}`}
                                    menu={[
                                        {
                                            key: 'remove',
                                            label: 'Remove override',
                                            icon: 'lucide:trash-2',
                                            danger: true,
                                            disabled: isRemoving,
                                            onPress: () => onRemove(override.id),
                                        },
                                    ]}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
